import type { InferUIMessageChunk, UIMessage } from "ai";

import type {
  AgentTurnDone,
  AgentTurnEvent,
  AgentTurnToolPayload,
} from "@/lib/ai/agent/run";

export type ChatToolEvent = AgentTurnToolPayload;

export const CONVERSATION_RANGES = [
  "all",
  "today",
  "yesterday",
  "week",
  "month",
  "lastMonth",
] as const;

export type ConversationRange = (typeof CONVERSATION_RANGES)[number];

export const SHARE_ACCESS = ["login", "public"] as const;

export type ShareAccess = (typeof SHARE_ACCESS)[number];

export type ConversationRunStatus = "running" | "success" | "failed" | null;

export const MAX_CHAT_CONTENT_CHARS = 20_000;

export type ChatStreamMeta = {
  conversationId: string;
  modelId: string;
  modelLabel: string;
  billed: boolean;
  agentId: string | null;
};

export type ChatStreamDone = AgentTurnDone;

export type ChatUiDataParts = {
  meta: ChatStreamMeta;
  tool: ChatToolEvent;

  title: { title: string | null; conversationId?: string; pending?: boolean };
};

export type ChatUIMessage = UIMessage<ChatStreamDone, ChatUiDataParts>;

export function createChatChunkWriter(
  write: (chunk: InferUIMessageChunk<ChatUIMessage>) => void,
) {

  let textSeq = 0;
  let textPartId = "";

  const reasoningPartId = "reasoning-0";

  let textOpen = false;
  let reasoningOpen = false;

  function openText() {
    if (!textOpen) {
      textSeq += 1;
      textPartId = `text-${textSeq}`;
      write({ type: "text-start", id: textPartId });
      textOpen = true;
    }
  }

  function closeText() {
    if (textOpen) {
      write({ type: "text-end", id: textPartId });
      textOpen = false;
    }
  }

  function openReasoning() {
    if (!reasoningOpen) {
      write({ type: "reasoning-start", id: reasoningPartId });
      reasoningOpen = true;
    }
  }

  function closeReasoning() {
    if (reasoningOpen) {
      write({ type: "reasoning-end", id: reasoningPartId });
      reasoningOpen = false;
    }
  }

  return {
    onEvent(event: AgentTurnEvent) {
      switch (event.type) {
        case "meta":

          write({
            type: "data-meta",

            transient: true,
            data: {
              conversationId: event.conversationId,
              modelId: event.modelId,
              modelLabel: event.modelLabel,
              billed: event.billed,
              agentId: event.agentId,
            },
          });
          break;
        case "delta":
          openText();
          write({ type: "text-delta", id: textPartId, delta: event.text });
          break;

        case "reasoning-start":
          openReasoning();
          break;
        case "reasoning-delta":

          openReasoning();
          write({
            type: "reasoning-delta",
            id: reasoningPartId,
            delta: event.text,
          });
          break;
        case "reasoning-end":
          closeReasoning();
          break;

        case "file":
          closeReasoning();
          closeText();
          write({
            type: "file",
            mediaType: event.mediaType,
            url: event.url,
          });
          break;

        case "source":
          write({
            type: "source-url",
            sourceId: event.url,
            url: event.url,
            title: event.title || undefined,
          });
          break;
        case "tool":

          write({ type: "data-tool", transient: true, data: event.payload });
          break;
        case "error":
          write({ type: "error", errorText: event.message });
          break;
        case "done":

          closeReasoning();
          closeText();

          write({ type: "message-metadata", messageMetadata: event.payload });
          break;
      }
    },

    finish() {
      closeReasoning();
      closeText();
    },
  };
}

export type ChatToolNoteKey = "callingPaid" | "calling" | "called" | "failed";

export type ChatToolNoteFormat = (
  key: ChatToolNoteKey,
  values: { label: string; points: number },
) => string;

const zhToolNote: ChatToolNoteFormat = (key, { label, points }) =>
  ({
    callingPaid: `正在调用「${label}」（${points} 积分）…`,
    calling: `正在调用「${label}」…`,
    called: `已调用「${label}」`,
    failed: `「${label}」调用失败`,
  })[key];

export function chatToolNote(
  event: ChatToolEvent,
  format: ChatToolNoteFormat = zhToolNote,
): string {
  const values = { label: event.label, points: event.costPoints };

  if (event.status === "start") {
    return format(event.costPoints > 0 ? "callingPaid" : "calling", values);
  }

  return format(event.status === "ok" ? "called" : "failed", values);
}

export function chatMessageText(message: ChatUIMessage): string {
  return message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("");
}

type StoredFile = {
  mediaType: string;
  url: string;
  filename?: string;
  attachId?: string;
  offset?: number;
};

function toFilePart(file: StoredFile) {
  return {
    type: "file" as const,
    mediaType: file.mediaType,
    url: file.url,
    filename: file.filename,

    ...(file.attachId ? { providerMetadata: { attach: { id: file.attachId } } } : {}),
  };
}

function interleaveFiles(content: string, files: StoredFile[]) {
  const placed = files
    .filter((file) => typeof file.offset === "number")
    .sort((left, right) => (left.offset ?? 0) - (right.offset ?? 0));
  const parts: (ReturnType<typeof toFilePart> | { type: "text"; text: string })[] = [];
  let cursor = 0;

  for (const file of placed) {
    const offset = Math.min(Math.max(file.offset ?? 0, cursor), content.length);

    if (offset > cursor) parts.push({ type: "text", text: content.slice(cursor, offset) });

    parts.push(toFilePart(file));
    cursor = offset;
  }

  parts.push({ type: "text", text: content.slice(cursor) });

  return [
    ...parts,
    ...files.filter((file) => typeof file.offset !== "number").map(toFilePart),
  ];
}

export type ChatMessageSegment =
  | { kind: "text"; text: string }
  | { kind: "file"; url: string; mediaType: string };

export function chatMessageSegments(message: ChatUIMessage): ChatMessageSegment[] {
  const segments: ChatMessageSegment[] = [];

  for (const part of message.parts) {
    if (part.type === "text") {
      const last = segments.at(-1);

      if (last?.kind === "text") last.text += part.text;
      else if (part.text) segments.push({ kind: "text", text: part.text });
    } else if (part.type === "file") {
      segments.push({ kind: "file", url: part.url, mediaType: part.mediaType });
    }
  }

  return segments;
}

export function toUIMessage(row: {
  id: string;
  role: string;
  content: string;

  reasoning?: string;

  files?:
    | {
        mediaType: string;
        url: string;
        filename?: string;
        attachId?: string;
        offset?: number;
      }[]
    | null;

  sources?: { url: string; title: string }[] | null;
  inputTokens?: number;
  cachedTokens?: number;
  cacheWriteTokens?: number;
  outputTokens?: number;
  costPoints?: number;

  toolTrace?: ChatToolEvent[] | null;
  aborted?: boolean;
  createdAt?: string;

  durationMs?: number;

  error?: string;
}): ChatUIMessage {
  const completedTools = row.role === "assistant"
    ? (row.toolTrace ?? []).filter((tool) => tool.status !== "start") : [];
  const tokenCostPoints = row.costPoints ?? 0;
  const toolCostPoints = completedTools.reduce((sum, tool) => sum + tool.costPoints, 0);
  const mcpTools = completedTools.filter((tool) => !tool.kind);
  const skillTools = completedTools.filter((tool) => tool.kind === "skill");

  return {
    id: row.id,
    role: row.role === "user" ? "user" : "assistant",

    parts: [
      ...(row.reasoning
        ? [
            {
              type: "reasoning" as const,
              text: row.reasoning,
              state: "done" as const,
            },
          ]
        : []),
      ...interleaveFiles(row.content, row.files ?? []),
      ...(row.sources ?? []).map((source) => ({
        type: "source-url" as const,
        sourceId: source.url,
        url: source.url,
        title: source.title || undefined,
      })),
    ],
    metadata: {
      inputTokens: row.inputTokens ?? 0,
      cachedTokens: row.cachedTokens ?? 0,
      cacheWriteTokens: row.cacheWriteTokens ?? 0,
      outputTokens: row.outputTokens ?? 0,
      costPoints: tokenCostPoints + toolCostPoints,
      aborted: row.aborted ?? false,
      createdAt: row.createdAt,
      durationMs: row.durationMs,
      ...(row.error ? { error: row.error } : {}),

      tokenCostPoints,
      mcpCalls: mcpTools.length,
      mcpCostPoints: mcpTools.reduce((sum, tool) => sum + tool.costPoints, 0),
      skillCalls: skillTools.length,
      skillCostPoints: skillTools.reduce((sum, tool) => sum + tool.costPoints, 0),
    },
  };
}

export function mergeToolEvent(
  list: ChatToolEvent[],
  event: ChatToolEvent,
): ChatToolEvent[] {

  const index = list.findIndex((item) =>
    event.callId ? item.callId === event.callId : item.label === event.label,
  );

  if (index === -1) {
    return [...list, event];
  }

  const next = [...list];
  next[index] = event;

  return next;
}

export type ChatErrorKind =
  | "busy"
  | "tooLong"
  | "timeout"
  | "unavailable"
  | "unsafe"
  | "network"
  | "noImage"
  | "settleFailed"
  | "interrupted"
  | "attachmentUnreadable"
  | "failed";

export function chatErrorKind(raw: string, own = false): ChatErrorKind | null {
  const text = raw.trim();

  if (/rate.?limit|too many requests|\b429\b|限流|频率/i.test(text)) {
    return "busy";
  }

  if (/context.?length|maximum context|too many tokens|token limit|context window|超出.*长度/i.test(text)) {
    return "tooLong";
  }

  if (/timed? ?out|timeout|超时/i.test(text)) {
    return "timeout";
  }

  if (/\b(401|403)\b|api.?key|unauthori[sz]ed|insufficient.?(quota|balance)|quota exceeded|billing/i.test(text)) {
    return "unavailable";
  }

  if (/content.?(filter|policy)|safety|sensitive|inappropriate|违规|审核/i.test(text)) {
    return "unsafe";
  }

  if (/fetch failed|failed to fetch|network|econn|socket/i.test(text)) {
    return "network";
  }

  if (/没有返回图片/.test(text)) {
    return "noImage";
  }

  if (/^结算失败/.test(text)) {
    return "settleFailed";
  }

  if (/^对话中断/.test(text)) {
    return "interrupted";
  }

  if (/^附件「.*」读不出文字/.test(text)) {
    return "attachmentUnreadable";
  }

  if (/^模型调用失败/.test(text)) {
    return "failed";
  }

  if (own || (/[\u4e00-\u9fff]/.test(text) && text.length <= 120)) {
    return null;
  }

  return "failed";
}

export function isOwnChatError(raw: string): boolean {
  const text = raw.trim();
  return !text || text.startsWith("{");
}

export function readChatErrorMessage(raw: string, fallback: string): string {
  const text = raw.trim();

  if (!text) {
    return fallback;
  }

  if (!text.startsWith("{")) {
    return text;
  }

  try {
    const parsed = JSON.parse(text) as {
      error?: { message?: unknown };
      message?: unknown;
    };
    const message = parsed.error?.message ?? parsed.message;

    return typeof message === "string" && message.trim()
      ? message.trim()
      : fallback;
  } catch {

    return text;
  }
}

export type ChatTurnStep = {

  key: string;
  kind: "reasoning" | "tool" | "file" | "answer";
  status: "complete" | "active" | "pending";

  tool?: ChatToolEvent;
};

export function buildTurnSteps(input: {

  hasReasoning: boolean;

  reasoningStreaming: boolean;
  tools: ChatToolEvent[];

  fileCount?: number;

  hasText: boolean;

  streaming: boolean;
}): ChatTurnStep[] {
  const steps: ChatTurnStep[] = [];

  if (input.hasReasoning) {
    steps.push({
      key: "reasoning",
      kind: "reasoning",
      status: input.reasoningStreaming ? "active" : "complete",
    });
  }

  for (const tool of input.tools) {
    steps.push({

      key: "tool:" + (tool.callId ?? tool.label),
      kind: "tool",
      status: tool.status === "start" ? "active" : "complete",
      tool,
    });
  }

  if ((input.fileCount ?? 0) > 0) {
    steps.push({
      key: "files",
      kind: "file",

      status: "complete",
    });
  }

  const answering = input.hasText;

  if (input.streaming || answering || steps.length === 0) {
    steps.push({
      key: "answer",
      kind: "answer",
      status: !input.streaming
        ? answering
          ? "complete"
          : "pending"
        : answering
          ? "active"
          : "pending",
    });
  }

  return steps;
}
